<script setup lang="ts">
import { preloadComponents } from '#app'
import type { Game, GameClickEventEmit, GameListProps, GameTilePosition } from '@game/types'
import { useGameList, useI18n } from '#imports'

const { t } = useI18n()

const props = withDefaults(defineProps<GameListProps>(), {
    showAllButton: false,
    forceShowAllButton: false,
    showNewGames: true,
    hasSidebar: false,
    videoEnabled: true,
    enableJackpots: 'auto',
    maxColumns: 5,
    titleTag: 'h1',
})

const emit = defineEmits<GameClickEventEmit & {
    'load-more-games': [category: string]
}>()

// Provides list-scoped jackpot/promotion queries and a shared opening-hours
// clock to every <GameTile> rendered under this list — avoids each tile
// registering its own useAsyncData / useCountdown instances.
useGameList(() => props.games, {
    enableJackpots: () => props.enableJackpots,
})

await preloadComponents([props.inlineRows ? 'GameListInline' : 'GameListGrid'])
</script>

<template>
    <section
        class="game-list__section"
        :class="{
            'game-list--inline': props.inlineRows,
            'game-list--mini-tiles': props.inlineRows && props.miniTiles,
            'game-list--tall': props.tileVariant === 'tall',
            'game-list--taller': props.tileVariant === 'taller',
        }"
    >
        <!-- eslint-disable vue/no-v-html, vue/no-v-text-v-html-on-component -- props.title is safe, and titleTag is always a native heading -->
        <component
            :is="props.titleTag"
            v-if="props.title"
            class="game-list__title"
            v-html="props.title"
        />
        <!-- eslint-enable vue/no-v-html, vue/no-v-text-v-html-on-component -->

        <GameListInline
            v-if="props.inlineRows"
            v-bind="props"
            @game-click="(game: Game, position?: GameTilePosition) => emit('game-click', game, position)"
        />

        <GameListGrid
            v-else
            v-bind="props"
            @load-more-games="emit('load-more-games', props.category)"
            @game-click="(game: Game, position?: GameTilePosition) => emit('game-click', game, position)"
        />

        <div v-if="props.forceShowAllButton || props.showAllButton" class="game-list__action">
            <hr>
            <slot name="showAllUrl">
                <UIButton color="secondary" v-bind="props.showAllLink" glitch>
                    <!-- eslint-disable-next-line vue/no-v-html props.showAllText & game.gameList.show_all is safe -->
                    <span v-html="props.showAllText || t('game.gameList.show_all')" />
                </UIButton>
            </slot>
            <hr>
        </div>
    </section>
</template>

<style lang="scss">
$ctaMinWidth: 212px;

.game-list {
    --max-columns: 5;

    display: grid;
    grid-auto-flow: dense;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 10px;

    @media (min-width: 500px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @include tablet(md) {
        margin-bottom: 15px;
        grid-template-columns: repeat(4, 1fr);
    }

    @include desktop {
        grid-template-columns: repeat(calc(var(--max-columns) - 1), 1fr);
    }

    @include desktop-xl {
        grid-template-columns: repeat(var(--max-columns), 1fr);
    }

    &--has-sidebar {
        @include desktop {
            grid-template-columns: repeat(calc(var(--max-columns) - 2), 1fr);
        }

        @include desktop-xl {
            grid-template-columns: repeat(calc(var(--max-columns) - 1), 1fr);
        }
    }

    &--inline {
        .game-list {
            display: block;
            margin-bottom: 10px;
            overflow-x: auto;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            overflow-y: hidden;
            transform: translateZ(0);
            scrollbar-color: transparent transparent;
            scrollbar-width: none;

            ::-webkit-scrollbar {
                display: none;
            }

            &__rows {
                display: grid;
                grid-auto-flow: column;
                grid-template-rows: repeat(var(--inline-rows, 1), auto);
                grid-auto-columns: max-content;
                gap: 10px;
            }

            &__container {
                position: relative;
                flex-direction: column;
                margin-right: -15px;
                padding-right: 10px;

                @include desktop {
                    margin-right: 0;
                    padding-right: 0;
                }
            }
        }

        .game-tile,
        .skeleton-item,
        .game-list__tile-slot {
            scroll-snap-type: start;
            width: 135px;
            height: 160px;

            @include desktop {
                width: 160px;
                height: 190px;
            }
        }

        .game-tile--video,
        .game-list__tile-slot--video {
            width: 280px;

            @include desktop {
                width: 330px;
            }
        }
    }

    &--mini-tiles {
        .game-tile,
        .skeleton-item,
        .game-list__tile-slot {
            width: 135px;
            height: 88px;

            @include desktop {
                width: 160px;
                height: 104px;
            }
        }
    }

    &--tall {
        .game-tile,
        .skeleton-item,
        .game-list__tile-slot {
            width: 220px;
            height: 308px;
        }
    }

    &--taller {
        .game-tile,
        .skeleton-item,
        .game-list__tile-slot {
            width: 220px;
            height: 460px;
        }
    }

    &__section {
        width: 100%;

        & + &,
        .custom-game-section + &,
        .games-with-sidebar + & {
            margin-top: 20px;

            @include tablet {
                margin-top: 30px;
            }
        }
    }

    &__action {
        display: flex;
        align-items: center;
        justify-content: center;
        place-items: center;
        margin-top: 30px;

        a,
        button {
            min-width: $ctaMinWidth;
            margin-right: 30px;
            margin-left: 30px;
            text-align: center;
            padding: 15px;
        }

        hr {
            flex-grow: 1;
        }
    }

    &__title {
        font-weight: $text-bold;
        font-size: 20px;
        margin-bottom: 13px;
        font-family: $brand-font-family;

        @include mobile {
            font-size: 22px;
        }

        @include mobile(sm) {
            font-size: 24px;
        }

        @include tablet(md) {
            font-size: 30px;
        }
    }
}

.skeleton-item {
    background-color: $skeleton-color;
    position: relative;
    z-index: 1;
    border-radius: var(--game-tile-border-radius, $game-tile-border-radius);
    overflow: hidden;
    aspect-ratio: 1;
    // Off-screen skeletons skip their contents, so the shimmer runs only near the viewport. The box is empty, so its size is unchanged.
    content-visibility: auto;

    &--video {
        grid-column: span 2;
        aspect-ratio: 2 / 1;
    }

    // Blurry loading line
    &::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        transform: translate3d(-100%, 0, 0);
        background-image: linear-gradient(90deg, transparent 0, $skeleton-loader-color 50%, transparent 100%);
        animation: skeleton-load 1.5s ease-in-out infinite;
    }
}

@keyframes skeleton-load {
    to {
        transform: translate3d(100%, 0, 0);
    }
}
</style>
